import React, { Suspense } from "react";
import { createBrowserRouter, Navigate, Outlet } from "react-router";
import { rootLoader } from "@/loaders/root-loader";
import { DEFAULT_PROMPT_FEATURE } from "@/utils/onpPromptCatalog";

// Lazy-loaded
const AdminLayout = React.lazy(() => import("./components/AdminLayout"));
const Dashboard = React.lazy(() => import("./pages/Dashboard/index"));
const Shortcuts = React.lazy(() => import("./pages/Shortcuts/index"));
// PRODUCT_DEV-493
const EditMetrics = React.lazy(() => import("./pages/EditMetrics/index"));
// PRODUCT_DEV-506
const MemoUsage = React.lazy(() => import("./pages/MemoUsage/index"));
// PRODUCT_DEV-506(review): 計測・ログ（編集ログ / メモ・ToDo をページ内トグルで切替）
const MetricsLogs = React.lazy(() => import("./pages/MetricsLogs/index"));
// PRODUCT_DEV-653: 開発量の可観測性
const DevMetrics = React.lazy(() => import("./pages/DevMetrics/index"));
const Cost = React.lazy(() => import("./pages/Cost/index"));
const Tenants = React.lazy(() => import("./pages/Tenants/index"));
const TenantDetail = React.lazy(() => import("./pages/Tenants/[tenantId]"));
// PRODUCT_DEV-708: ONP設定 / 商談記録設定 / 顧客レポート設定の 3 ページを
// 「機能 → カード → 編集ペイン」の 1 ページへ統合した
const PromptWorkbench = React.lazy(
  () => import("./pages/ONPSettings/PromptWorkbench"),
);
const Products = React.lazy(() => import("./pages/Products/index"));
// PRODUCT_DEV-356: 全プロンプト ハッシュ一覧
const HashList = React.lazy(() => import("./pages/ONPSettings/HashList"));
// PRODUCT_DEV-708: 辞書と Playground をプロンプト編集ページから独立タブへ分離
const DictionaryPage = React.lazy(
  () => import("./pages/ONPSettings/DictionaryPage"),
);
const PlaygroundPage = React.lazy(
  () => import("./pages/ONPSettings/PlaygroundPage"),
);
const OperatorAnnouncements = React.lazy(
  () => import("./pages/OperatorAnnouncements/index"),
);
// PRODUCT_DEV-379
const NotificationTemplates = React.lazy(
  () => import("./pages/NotificationTemplates/index"),
);
const NotificationTemplateEdit = React.lazy(
  () => import("./pages/NotificationTemplates/[templateId]"),
);
// PRODUCT_DEV-466
const SharedFiles = React.lazy(() => import("./pages/SharedFiles/index"));
// PRODUCT_DEV-413
const AgenticJobs = React.lazy(() => import("./pages/AgenticJobs/index"));
const AgenticJobCreate = React.lazy(
  () => import("./pages/AgenticJobs/create"),
);
const AgenticJobCreateSelfLoop = React.lazy(
  () => import("./pages/AgenticJobs/create-self-loop"),
);
const AgenticJobDetail = React.lazy(
  () => import("./pages/AgenticJobs/[jobId]"),
);
const AgenticSettings = React.lazy(
  () => import("./pages/AgenticSettings/index"),
);
// PRODUCT_DEV-413 Phase 2.x: Workflows (jobKind=workflow テンプレ管理)
const Workflows = React.lazy(() => import("./pages/Workflows/index"));
const WorkflowEdit = React.lazy(() => import("./pages/Workflows/edit"));
// PRODUCT_DEV-434
const VectorStoreInspector = React.lazy(
  () => import("./pages/VectorStoreInspector/index"),
);
const VectorStoreInspectorTenant = React.lazy(
  () => import("./pages/VectorStoreInspector/[tenantId]"),
);
const VectorStoreInspectorChunk = React.lazy(
  () => import("./pages/VectorStoreInspector/Chunk"),
);
// PRODUCT_DEV-494: 環境間データ移行 (dev/stg のみ)
const EnvMigration = React.lazy(() => import("./pages/EnvMigration/index"));
// PRODUCT_DEV-635: 問い合わせ管理
const Inquiries = React.lazy(() => import("./pages/Inquiries/index"));
const InquiryDetail = React.lazy(() => import("./pages/Inquiries/[inquiryId]"));

const RootLayout: React.FC = () => (
  <Suspense
    fallback={
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-500">Loading...</div>
      </div>
    }
  >
    <Outlet />
  </Suspense>
);

// nginx 経由(/vite/admin/)配信時は Vite の base(=VITE_BASE_PATH)配下で動くため、
// React Router にも同じ basename を渡す(PRODUCT_DEV-546、dev-lab/survey と同方式)。
// 従来はルート route の path に BASE_URL を入れていたが、その方式だと <Link to="/tenants">
// 等のオリジン絶対パスが /vite/admin プレフィックスを失い遷移不能だった。BASE_URL は
// 末尾スラッシュ付き(例 "/vite/admin/")なので basename 用に除去する。本番(base "/")は "/"。
const basename = import.meta.env.BASE_URL.replace(/\/+$/, "") || "/";

export const router = createBrowserRouter([
  {
    path: "/",
    id: "root",
    loader: rootLoader,
    element: <RootLayout />,
    children: [
      {
        element: <AdminLayout />,
        children: [
          { index: true, element: <Dashboard /> },
          { path: "shortcuts", element: <Shortcuts /> },
          { path: "metrics-logs", element: <MetricsLogs /> },
          { path: "dev-metrics", element: <DevMetrics /> },
          { path: "cost", element: <Cost /> },
          { path: "edit-metrics", element: <EditMetrics /> },
          { path: "memo-usage", element: <MemoUsage /> },
          { path: "tenants", element: <Tenants /> },
          { path: "tenants/:tenantId", element: <TenantDetail /> },
          // PRODUCT_DEV-708: 静的なタブを先に置く（:feature に食われないように）。
          // 旧 /onp-settings/meeting は不正スラッグとして PromptWorkbench 側で
          // 既定の機能へリダイレクトされる（/report はそのまま機能名と一致）。
          { path: "onp-settings/hashes", element: <HashList /> },
          { path: "onp-settings/dictionary", element: <DictionaryPage /> },
          { path: "onp-settings/playground", element: <PlaygroundPage /> },
          {
            path: "onp-settings",
            element: (
              <Navigate to={`/onp-settings/${DEFAULT_PROMPT_FEATURE}`} replace />
            ),
          },
          { path: "onp-settings/:feature", element: <PromptWorkbench /> },
          { path: "products", element: <Products /> },
          {
            path: "operator-announcements",
            element: <OperatorAnnouncements />,
          },
          // PRODUCT_DEV-635: 問い合わせ管理
          { path: "inquiries", element: <Inquiries /> },
          { path: "inquiries/:inquiryId", element: <InquiryDetail /> },
          {
            path: "notification-templates",
            element: <NotificationTemplates />,
          },
          {
            path: "notification-templates/:templateId",
            element: <NotificationTemplateEdit />,
          },
          { path: "shared-files", element: <SharedFiles /> },
          { path: "agentic-jobs", element: <AgenticJobs /> },
          { path: "agentic-jobs/create", element: <AgenticJobCreate /> },
          {
            path: "agentic-jobs/create-self-loop",
            element: <AgenticJobCreateSelfLoop />,
          },
          { path: "agentic-jobs/:jobId", element: <AgenticJobDetail /> },
          { path: "agentic-settings", element: <AgenticSettings /> },
          // PRODUCT_DEV-413 Phase 2.x: Workflows (jobKind=workflow テンプレ管理)
          { path: "workflows", element: <Workflows /> },
          { path: "workflows/new", element: <WorkflowEdit /> },
          { path: "workflows/:workflowId", element: <WorkflowEdit /> },
          {
            path: "vector-store-inspector",
            element: <VectorStoreInspector />,
          },
          {
            path: "vector-store-inspector/:tenantId",
            element: <VectorStoreInspectorTenant />,
          },
          {
            path: "vector-store-inspector/:tenantId/:meetingId/:chunkIdx",
            element: <VectorStoreInspectorChunk />,
          },
          // PRODUCT_DEV-494: 環境間データ移行 (dev/stg のみ。prod は環境ガードで弾く)
          { path: "env-migration", element: <EnvMigration /> },
        ],
      },
    ],
  },
], { basename });
